import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Breadcrumb from "@/components/layout/Breadcrumb";
import SeriesCard from "@/components/feature/SeriesCard";
import { seriesList, type Series } from "@/mocks/series";
import { useSeo } from "@/hooks/useSeo";

export default function SeriesDetail() {
  const { slug } = useParams();
  const series: Series | undefined = seriesList.find((s) => s.slug === slug);
  const idx = series ? seriesList.findIndex((s) => s.slug === series.slug) : -1;
  const prev = idx > 0 ? seriesList[idx - 1] : undefined;
  const next = idx >= 0 && idx < seriesList.length - 1 ? seriesList[idx + 1] : undefined;

  useSeo({
    title: series ? `${series.title}｜全网热瓜系列事件时间线` : "系列事件时间线",
    description: series
      ? `${series.summary.slice(0, 90)}本时间线按日期串联${series.episodes.length}个关键节点，相似剧情与后续走向一次摸清。`
      : "查看全网热瓜系列事件的完整时间线。",
    canonical: series ? `https://w01xfj.cn/shijian/${series.slug}` : undefined,
    jsonLd: series
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: series.title,
          description: series.tagline,
          articleSection: series.category,
          datePublished: series.episodes[0].date.replace(/\./g, "-"),
          keywords: `全网热瓜,系列事件,${series.category}`,
          author: { "@type": "Organization", name: "热瓜雷达" },
          publisher: { "@type": "Organization", name: "热瓜雷达" },
        }
      : undefined,
  });

  if (!series) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-container flex-col items-center justify-center px-4 text-center md:px-6">
          <h1 className="text-3xl text-foreground-950">没有找到这个系列</h1>
          <p className="mt-3 text-sm text-foreground-600">可能它还没被我们收录，或链接有误。</p>
          <Link to="/#series" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition hover:bg-primary-400">
            回到首页追系列
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <div className="mx-auto max-w-container px-4 pt-10 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "同类盘点", to: "/pandian" },
              { label: series.category },
              { label: series.title },
            ]}
          />
        </div>

        <article className="mx-auto max-w-container px-4 pt-6 md:px-6">
          <header className="overflow-hidden rounded-3xl border border-background-200 bg-background-50">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative min-h-[240px] w-full">
                <img src={series.cover} alt={`${series.title}封面`} className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50">
                  {series.category}
                </span>
              </div>
              <div className="flex flex-col justify-center p-6 md:p-10">
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  <span className={`rounded-full px-3 py-1 ${series.status === "已完结" ? "bg-background-200 text-foreground-700" : "bg-accent-100 text-accent-700"}`}>
                    {series.status}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {series.period}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-accent-500"></i>
                    {series.heat}
                  </span>
                </div>
                <h1 className="mt-4 text-2xl leading-snug text-foreground-950 md:text-3xl">{series.title}</h1>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{series.summary}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {series.cast.map((c) => (
                    <span key={c} className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </header>

          {/* 时间线 */}
          <section className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-2">
                <i className="ri-timeline-view text-xl text-primary-500"></i>
                <h2 className="text-xl text-foreground-950 md:text-2xl">事件完整时间线</h2>
                <span className="ml-1 text-sm text-foreground-500">（{series.episodes.length} 个节点）</span>
              </div>

              <ol className="mt-6 space-y-5">
                {series.episodes.map((ep, i) => (
                  <li key={ep.date} className="relative flex gap-4">
                    <div className="flex flex-col items-center">
                      <span
                        className={`font-display flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm text-background-50 ${
                          i === 0 ? "bg-accent-500" : "bg-primary-500"
                        }`}
                      >
                        {i + 1}
                      </span>
                      {i < series.episodes.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300"></span>}
                    </div>
                    <div className="mb-1 flex-1 rounded-2xl border border-background-200 bg-background-50 p-5">
                      <time className="font-display text-xs tracking-wide text-primary-600">{ep.date}</time>
                      <h3 className="mt-1.5 text-base font-semibold text-foreground-950">{ep.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-foreground-600">{ep.text}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <aside className="mt-8 rounded-2xl bg-accent-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 text-base font-semibold text-accent-900">
                  <i className="ri-eye-line"></i>
                  热瓜雷达怎么看
                </h2>
                <p className="mt-2 text-sm leading-7 text-foreground-800">{series.comment}</p>
              </aside>
            </div>

            {/* 侧栏 */}
            <aside className="space-y-6 lg:col-span-4">
              <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                <h2 className="text-base font-semibold text-foreground-950">吃瓜小贴士</h2>
                <ul className="mt-3 space-y-2 text-sm leading-6 text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-error-warning-line text-secondary-600"></i>
                    本时间线仅做信息梳理，不代表支持任何一方。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-links-line text-primary-500"></i>
                    关键节点均来自公开信源，传闻会单独标注。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-emotion-line text-accent-500"></i>
                    拒绝网暴、拒绝人肉，请理性看待网络信息。
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl bg-background-100/70 p-5">
                <h2 className="text-base font-semibold text-foreground-950">同栏目更多瓜</h2>
                <div className="mt-4 space-y-3">
                  {[prev, next].filter(Boolean).map((s) =>
                    s ? (
                      <Link key={s.slug} to={`/shijian/${s.slug}`} className="group flex items-center gap-3 rounded-xl bg-background-50 p-3 transition hover:-translate-y-0.5 hover:border hover:border-primary-300">
                        <span className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                          <img src={s.cover} alt={`${s.title}小图`} className="h-full w-full object-cover" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">{s.title}</span>
                          <span className="mt-0.5 block text-xs text-foreground-500">{s.status} · {s.heat}</span>
                        </span>
                      </Link>
                    ) : null,
                  )}
                </div>
                <Link to="/pandian" className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
                  去同类盘点找更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </section>

          <section className="mt-12">
            <h2 className="text-xl text-foreground-950 md:text-2xl">你可能还想追这些系列</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {seriesList.filter((s) => s.slug !== series.slug).map((s) => (
                <SeriesCard key={s.slug} series={s} />
              ))}
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}